import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { useModalCloseLock } from '../../modalCloseLock';
import { InputRadioTagTable } from '../../../display/tables/InputRadioTagTable';
import { tableProps as InputRadioTagTableProps } from '../../../display/tables/InputRadioTagTable/types';
import { tableIds } from '../../../display/tables/types';

// Data
import { GET_SEGMENT_TRANSFER_TARGET_PAGE } from '../../../../gql/queries/surveillanceRoute';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { adaptTransferTargetToOrder } from '../../../../utils/surveillanceRoute';
import {
  TARGET_HIDDEN_COLUMN_IDS,
  TARGET_HIDDEN_FILTER_IDS,
} from './targetTable';
import { FetchTargetPage, TargetPage, useTargetPage } from './useTargetPage';

// Types
import { ModalProps } from './types';
import type { SegmentTransferTarget } from '../../../../types/surveillanceRoute';

// Styles
import {
  BelowTable,
  ButtonContainer,
  CloseButton,
  Container,
  EmptyMessage,
  InlineError,
  MainButtonStyled,
  WarningText,
  PaginationButtonsStyled,
  Text,
} from './styles';
import { neighbourTime, pkWarningKey } from './targetTexts';

const hm = (iso: string) => format(new Date(iso), 'HH:mm');
const dayHm = (iso: string) => format(new Date(iso), 'dd/MM HH:mm');
const pk = (km?: number | null, meter?: number | null) =>
  km == null ? '—' : `${km}+${String(meter ?? 0).padStart(3, '0')}`;

/**
 * «Transferir tramo» (spec 2026-10-07 § 7): mueve un tramo cerrado al
 * recorrido donde debía estar. Los candidatos y si el tramo cabe en cada uno
 * los calcula el servidor con las mismas reglas que al transferir; aquí solo
 * se eligen.
 *
 * 14-oct (feedback del usuario): la tabla de selección de siempre
 * (`InputRadioTagTable` TAL CUAL), como el «Transferir» de actuaciones y
 * «Vincular tramo», en vez de tarjetas propias. Al marcar un recorrido, debajo
 * sale dónde quedará el tramo o, si no cabe, el motivo.
 *
 * 22-oct (revisión del usuario): también con sus filtros, su orden y sus
 * páginas, como las demás ventanas de transferir. Se abre sin filtros.
 */
export const TransferSegmentModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common', 'errors']);
  const theme = useTheme();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const segment = data?.segment;

  const [selectedId, setSelectedId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeLock = useModalCloseLock();
  // Uno por apertura del modal: reintentar desde aquí no transfiere dos veces.
  const clientOpId = useMemo(() => crypto.randomUUID(), []);

  const fetchPage = useMemo<FetchTargetPage | null>(
    () =>
      segment
        ? async ({ filter, skip, take }) => {
            const result = await client.query<{
              segmentTransferTargetPage: TargetPage;
            }>({
              query: GET_SEGMENT_TRANSFER_TARGET_PAGE,
              variables: { segmentId: segment.id, filter, skip, take },
              fetchPolicy: 'network-only',
            });
            return result.data.segmentTransferTargetPage;
          }
        : null,
    [client, segment]
  );
  const {
    targets,
    totalCount,
    page,
    pageCount,
    isLoading,
    loadFailed,
    onReFetchData,
    onReloadData,
    nextPage,
    previousPage,
  } = useTargetPage(tableIds.SEGMENT_TRANSFER_MODAL, fetchPage);

  const selected = targets.find((target) => target.routeId === selectedId);

  // La hora del tramo de al lado, con su fecha si es de otro día (30-oct).
  const when = (iso: string, sameDayAs?: string | null) => {
    const { time, date } = neighbourTime(iso, sameDayAs);
    return date
      ? t('pages:surveillance.transferSegment.position.dayOf', { time, date })
      : time;
  };
  const position = (target: SegmentTransferTarget): string => {
    const prevRef = segment?.startDate;
    const nextRef = segment?.endDate ?? segment?.startDate;
    if (target.prevEnd && target.nextStart) {
      return t('pages:surveillance.transferSegment.position.between', {
        prev: when(target.prevEnd, prevRef),
        next: when(target.nextStart, nextRef),
      });
    }
    if (target.nextStart) {
      return t('pages:surveillance.transferSegment.position.first', {
        next: when(target.nextStart, nextRef),
      });
    }
    if (target.prevEnd) {
      return t('pages:surveillance.transferSegment.position.last', {
        prev: when(target.prevEnd, prevRef),
      });
    }
    return t('pages:surveillance.transferSegment.position.only');
  };

  const onConfirm = useCallback(async () => {
    if (!data?.onConfirm || !selected?.fits || isSubmitting) return;
    setIsSubmitting(true);
    // Mientras viaja no se cierra (QA 15-oct, como «Registrar parada»).
    closeLock.lock();
    // El cargador de la propia tabla, como en «Vincular tramo».
    dispatch(loadingData(tableIds.SEGMENT_TRANSFER_MODAL, true));
    let done = false;
    try {
      await data.onConfirm(selected, clientOpId);
      done = true;
    } catch {
      // El toast del error ya lo emitió el hook; el modal sigue abierto.
    } finally {
      dispatch(loadingData(tableIds.SEGMENT_TRANSFER_MODAL, false));
      closeLock.unlock();
      setIsSubmitting(false);
    }
    if (done) onClickClose();
  }, [
    clientOpId,
    closeLock,
    data,
    dispatch,
    isSubmitting,
    onClickClose,
    selected,
  ]);

  const cause = segment?.pauseCause
    ? t(`pages:surveillance.pause.causes.${segment.pauseCause}`, {
        defaultValue: segment.pauseCause,
      })
    : '';
  const title = segment
    ? `${t('pages:surveillance.transferSegment.title')} · Nº ${
        data?.segmentNumber ?? ''
      } · ${dayHm(segment.startDate)}–${
        segment.endDate ? hm(segment.endDate) : '—'
      } · ${pk(segment.pkInitKm, segment.pkInitMeter)} → ${pk(
        segment.pkEndKm,
        segment.pkEndMeter
      )}${cause ? ` · ${cause}` : ''}`
    : t('pages:surveillance.transferSegment.title');

  const rows = useMemo(
    () => targets.map(adaptTransferTargetToOrder),
    [targets]
  );
  const tableData = useMemo(
    () => ({
      genericOrders: rows,
      filtersDataOptions: {
        concessions: [],
        capitols: [],
        subCapitols: [],
        classifications: [],
      },
      totalCount,
      pageInfo: {
        hasNextPage: page < pageCount,
        hasPreviousPage: page > 1,
      },
      loading: isLoading,
    }),
    [rows, totalCount, page, pageCount, isLoading]
  );

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container data-testid="transfer-segment-modal">
        <InputRadioTagTable
          titleText={title}
          data={tableData as unknown as InputRadioTagTableProps['data']}
          setRowState={setSelectedId}
          onReFetchData={onReFetchData}
          onReloadData={onReloadData}
          onReFetchPagination={() => undefined}
          tableId={tableIds.SEGMENT_TRANSFER_MODAL}
          hiddenColumnIds={TARGET_HIDDEN_COLUMN_IDS}
          hiddenFilterIds={TARGET_HIDDEN_FILTER_IDS}
          // La tabla pinta la «Fecha final»: se filtra por ella como en
          // «Vincular tramo» (revisión del 27-oct).
          showEndDateFilter
          showRouteNameColumn
          showButtonsPagination={false}
          onRowDoubleTap={(row) => {
            // Solo marca: transferir es un gesto aparte.
            if (row.original.id) setSelectedId(row.original.id);
          }}
        />
        {!isLoading && (loadFailed || targets.length === 0) && (
          <EmptyMessage data-testid="transfer-no-targets">
            {loadFailed
              ? t('errors:generalError')
              : t('pages:surveillance.transferSegment.noTargets')}
          </EmptyMessage>
        )}

        <BelowTable>
          {selected && selected.fits && (
            <Text size="S" variant="semiBold" data-testid="transfer-position">
              {position(selected)}
            </Text>
          )}
          {/* 30-oct (el usuario): los avisos, centrados como el resto de
              mensajes de debajo de la tabla, no en una caja naranja. */}
          {selected?.fits && selected.pkWarningCode && (
            <WarningText
              size="S"
              role="status"
              data-testid="transfer-pk-warning"
            >
              {t(pkWarningKey(selected.pkWarningCode))}
            </WarningText>
          )}
          {selected && !selected.fits && (
            <InlineError size="S" data-testid="transfer-target-reason">
              {selected.reasonCode
                ? t(`errors:backend.${selected.reasonCode}`, {
                    defaultValue: t('errors:generalError'),
                  })
                : t('errors:generalError')}
            </InlineError>
          )}
          {segment?.linkedBasicActionOrder?.id && (
            <WarningText
              size="S"
              role="status"
              data-testid="transfer-warning-basic"
            >
              {t('pages:surveillance.transferSegment.warningBasic')}
            </WarningText>
          )}
          {data?.routeSegmentsCount === 1 && (
            <WarningText
              size="S"
              role="status"
              data-testid="transfer-warning-empty"
            >
              {t('pages:surveillance.transferSegment.warningEmpty')}
            </WarningText>
          )}
        </BelowTable>

        <ButtonContainer>
          <CloseButton
            text={t('common:buttons.back')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <PaginationButtonsStyled
            pageCount={pageCount}
            currentPage={page}
            totalCount={totalCount}
            nextPage={nextPage}
            previousPage={previousPage}
            isDisabled={isLoading || isSubmitting}
          />
          <MainButtonStyled
            text={t('pages:surveillance.transferSegment.confirm')}
            onClick={() => void onConfirm()}
            disabled={isSubmitting || !selected?.fits}
            data-testid="transfer-confirm"
          />
        </ButtonContainer>
      </Container>
    </ModalContainer>
  );
};

export default TransferSegmentModal;
